// ABOUTME: Watermark components for PDF exports and browser print output.
// ABOUTME: Renders tier-based "Built with Oleum BI" branding on free-tier reports.

/**
 * Subscription tiers that show the per-page footer watermark.
 * freemium and base_individual both get the footer.
 */
const FOOTER_WATERMARK_TIERS = ['freemium', 'base_individual'];

/**
 * Subscription tiers that show the full last-page attribution block.
 * Only freemium gets the attribution.
 */
const ATTRIBUTION_TIERS = ['freemium'];

interface WatermarkProps {
  subscriptionTier?: string | null;
}

/**
 * Per-page footer watermark rendered at the bottom of each print page.
 * Shows "Built with Oleum BI -- bi.oleum.pro" in light gray 8pt text.
 */
export function PageFooterWatermark({ subscriptionTier }: WatermarkProps) {
  if (!subscriptionTier || !FOOTER_WATERMARK_TIERS.includes(subscriptionTier)) {
    return null;
  }

  return (
    <div
      className="print-watermark-footer"
      style={{
        position: 'absolute',
        bottom: '0.15in',
        left: 0,
        right: 0,
        textAlign: 'center',
        fontSize: '8pt',
        color: '#9ca3af',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        letterSpacing: '0.02em',
        pointerEvents: 'none',
      }}
    >
      Built with Oleum BI &mdash; bi.oleum.pro
    </div>
  );
}

/**
 * Last-page attribution block with Oleum BI logo and description.
 * Only shown for freemium tier. Renders after the final report page.
 */
export function LastPageAttribution({ subscriptionTier }: WatermarkProps) {
  if (!subscriptionTier || !ATTRIBUTION_TIERS.includes(subscriptionTier)) {
    return null;
  }

  return (
    <div
      className="print-attribution"
      style={{
        pageBreakBefore: 'auto',
        marginTop: '2em',
        paddingTop: '1.5em',
        borderTop: '1px solid #e5e7eb',
        textAlign: 'center',
        fontSize: '9pt',
        color: '#6b7280',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          marginBottom: '0.75em',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '0.5em',
        }}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ opacity: 0.5 }}
        >
          <path
            d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"
            stroke="#9ca3af"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span style={{ fontWeight: 600, fontSize: '10pt', color: '#6b7280' }}>
          Oleum BI
        </span>
      </div>
      <div style={{ marginBottom: '0.25em' }}>
        This report was generated with Oleum BI
      </div>
      <div style={{ fontSize: '8pt', color: '#9ca3af' }}>
        AI-powered business intelligence &mdash; bi.oleum.pro
      </div>
    </div>
  );
}

/**
 * CSS styles for @media print watermark on shared report pages.
 * Injected as a style block. Adds the same footer watermark
 * that appears in the Playwright PDF when users Ctrl+P from the browser.
 */
export function PrintMediaWatermarkStyles({ subscriptionTier }: WatermarkProps) {
  if (!subscriptionTier || !FOOTER_WATERMARK_TIERS.includes(subscriptionTier)) {
    return null;
  }

  return (
    <style>{`
      @media print {
        body::after {
          content: "Built with Oleum BI \\2014 bi.oleum.pro";
          position: fixed;
          bottom: 0.15in;
          left: 0;
          right: 0;
          text-align: center;
          font-size: 8pt;
          color: #9ca3af;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          letter-spacing: 0.02em;
          z-index: 9999;
        }
      }
    `}</style>
  );
}
